import { create } from 'zustand'
import * as THREE from 'three'
import { computeVariedColor } from '../utils/colorVariation'

export interface MaterialData {
  name: string
  color: string
  roughness: number
  metalness: number
  emissive: string
  emissiveIntensity: number
  opacity: number
  transparent: boolean
  // Per-mesh color variation (0-1). When > 0, each mesh gets a deterministic lightness offset.
  colorVariation?: number
  // Texture properties
  map?: string | null // diffuse/color texture URL
  normalMap?: string | null // normal map URL
  roughnessMap?: string | null // roughness texture URL
  metalnessMap?: string | null // metalness texture URL
  emissiveMap?: string | null // emissive texture URL
  alphaMap?: string | null // opacity/alpha texture URL
  // Texture tiling (repeat)
  textureRepeat?: { x: number; y: number } // Default is {x: 1, y: 1}
}

interface MaterialState {
  // Materials map: original name -> material data
  materials: Map<string, MaterialData>

  // Three.js material instances
  materialInstances: Map<string, THREE.MeshStandardMaterial>

  // Per-mesh variation clones: materialName -> Map<meshName, clonedMaterial>
  variationClones: Map<string, Map<string, THREE.MeshStandardMaterial>>

  // Default materials loaded from JSON
  defaultMaterials: Record<string, Partial<MaterialData>> | null

  // Actions
  initializeMaterials: (materialNames: string[], skipTextures?: boolean) => void
  loadDefaultMaterials: () => Promise<void>
  updateMaterial: (name: string, properties: Partial<MaterialData>) => void
  getMaterial: (name: string) => MaterialData | undefined
  getThreeMaterial: (name: string) => THREE.MeshStandardMaterial | undefined

  // Variation clone management
  createVariedClone: (
    materialName: string,
    meshName: string
  ) => THREE.MeshStandardMaterial | undefined
  getVariedClone: (materialName: string, meshName: string) => THREE.MeshStandardMaterial | undefined
  clearVariationClones: (materialName: string) => void

  // Texture management
  loadTexture: (materialName: string, textureType: keyof MaterialData, file: File) => Promise<void>
  removeTexture: (materialName: string, textureType: keyof MaterialData) => void

  // Import/Export
  exportToJSON: () => string
  importFromJSON: (json: string) => void
  downloadJSON: () => void
  copyJSONToClipboard: () => void

  // Utilities
  randomizeAllColors: () => void
  resetAllToDefaults: () => void

  // Persistence
  isDirty: boolean
  isSaving: boolean
  saveToServer: () => Promise<void>
  loadFromServer: () => Promise<void>
}

// Helper to generate random color
const randomColor = () => {
  const colors = [
    '#FF6B6B',
    '#4ECDC4',
    '#45B7D1',
    '#96CEB4',
    '#FECA57',
    '#FF9FF3',
    '#54A0FF',
    '#48DBFB',
    '#0ABDE3',
    '#EE5A24',
    '#F8B500',
    '#A55EEA',
    '#26DE81',
    '#FD79A8',
    '#FDCB6E',
  ]
  return colors[Math.floor(Math.random() * colors.length)]
}

// Default material properties
const createDefaultMaterial = (name: string): MaterialData => ({
  name,
  color: '#808080',
  roughness: 0.5,
  metalness: 0.0,
  emissive: '#000000',
  emissiveIntensity: 0,
  opacity: 1,
  transparent: false,
  colorVariation: 0,
  map: null,
  normalMap: null,
  roughnessMap: null,
  metalnessMap: null,
  emissiveMap: null,
  alphaMap: null,
  textureRepeat: { x: 1, y: 1 },
})

export const useMaterialStore = create<MaterialState>((set, get) => ({
  materials: new Map(),
  materialInstances: new Map(),
  variationClones: new Map(),
  defaultMaterials: null,
  isDirty: false,
  isSaving: false,

  loadDefaultMaterials: async () => {
    try {
      const response = await fetch('/default-materials.json')
      const data = await response.json()

      if (data && data.materials) {
        set({ defaultMaterials: data.materials })
        console.log('Loaded default materials from JSON:', Object.keys(data.materials))
      }
    } catch (error) {
      console.warn('Could not load default materials, will use random colors:', error)
    }
  },

  initializeMaterials: (materialNames: string[], skipTextures = false) => {
    // Dispose all existing variation clones
    const { variationClones: existingClones } = get()
    existingClones.forEach((clones) => {
      clones.forEach((clone) => clone.dispose())
    })

    const materials = new Map<string, MaterialData>()
    const materialInstances = new Map<string, THREE.MeshStandardMaterial>()
    const variationClones = new Map<string, Map<string, THREE.MeshStandardMaterial>>()
    const { defaultMaterials } = get()

    materialNames.forEach((name) => {
      let materialData: MaterialData

      // Check if we have default settings for this material
      if (defaultMaterials && defaultMaterials[name]) {
        // Use settings from JSON file
        const defaults = createDefaultMaterial(name)
        materialData = {
          ...defaults,
          ...defaultMaterials[name],
          name, // Ensure name is always correct
        }
        console.log(`Loaded material "${name}" from default settings`)
      } else {
        // No matching material in JSON, create with random color
        materialData = createDefaultMaterial(name)
        console.log(`Created new material "${name}" with random color (not in default JSON)`)
      }

      materials.set(name, materialData)

      // Create Three.js material
      const threeMaterial = new THREE.MeshStandardMaterial({
        name,
        color: materialData.color,
        roughness: materialData.roughness,
        metalness: materialData.metalness,
        emissive: materialData.emissive,
        emissiveIntensity: materialData.emissiveIntensity,
        opacity: materialData.opacity,
        transparent: materialData.transparent,
      })

      // Load textures if they exist in the material data (skipped when server config will override)
      if (!skipTextures) {
        const textureLoader = new THREE.TextureLoader()
        const textureRepeat = materialData.textureRepeat || { x: 1, y: 1 }

        const loadTexture = (url: string, isColorTexture = false) => {
          return textureLoader.load(url, (texture) => {
            if (isColorTexture) {
              texture.colorSpace = THREE.SRGBColorSpace
            }
            texture.wrapS = THREE.RepeatWrapping
            texture.wrapT = THREE.RepeatWrapping
            texture.repeat.set(textureRepeat.x, textureRepeat.y)
          })
        }

        if (materialData.map) {
          threeMaterial.map = loadTexture(materialData.map, true)
        }
        if (materialData.normalMap) {
          threeMaterial.normalMap = loadTexture(materialData.normalMap)
        }
        if (materialData.roughnessMap) {
          threeMaterial.roughnessMap = loadTexture(materialData.roughnessMap)
        }
        if (materialData.metalnessMap) {
          threeMaterial.metalnessMap = loadTexture(materialData.metalnessMap)
        }
        if (materialData.emissiveMap) {
          threeMaterial.emissiveMap = loadTexture(materialData.emissiveMap)
        }
        if (materialData.alphaMap) {
          threeMaterial.alphaMap = loadTexture(materialData.alphaMap)
          threeMaterial.transparent = true
        }
      }

      materialInstances.set(name, threeMaterial)
    })

    set({ materials, materialInstances, variationClones })
  },

  updateMaterial: (name: string, properties: Partial<MaterialData>) => {
    const { materials, materialInstances } = get()
    const material = materials.get(name)
    const threeMaterial = materialInstances.get(name)

    if (material && threeMaterial) {
      // Update material data
      const updated = { ...material, ...properties }
      materials.set(name, updated)

      // Update Three.js material
      if (properties.color !== undefined) {
        threeMaterial.color.set(properties.color)
      }
      if (properties.roughness !== undefined) {
        threeMaterial.roughness = properties.roughness
      }
      if (properties.metalness !== undefined) {
        threeMaterial.metalness = properties.metalness
      }
      if (properties.emissive !== undefined) {
        threeMaterial.emissive.set(properties.emissive)
      }
      if (properties.emissiveIntensity !== undefined) {
        threeMaterial.emissiveIntensity = properties.emissiveIntensity
      }
      if (properties.opacity !== undefined) {
        threeMaterial.opacity = properties.opacity
      }
      if (properties.transparent !== undefined) {
        threeMaterial.transparent = properties.transparent
      }

      // Handle texture updates
      const textureLoader = new THREE.TextureLoader()

      // Get texture repeat settings
      const textureRepeat = material.textureRepeat || { x: 1, y: 1 }

      type TextureKey =
        | 'map'
        | 'normalMap'
        | 'roughnessMap'
        | 'metalnessMap'
        | 'emissiveMap'
        | 'alphaMap'

      // Helper function to load and apply texture (also propagates to variation clones)
      const applyTexture = (
        url: string | null | undefined,
        textureKey: TextureKey,
        isColorTexture = false
      ) => {
        if (url) {
          textureLoader.load(url, (texture) => {
            if (isColorTexture) {
              texture.colorSpace = THREE.SRGBColorSpace
            }
            // Apply tiling
            texture.wrapS = THREE.RepeatWrapping
            texture.wrapT = THREE.RepeatWrapping
            texture.repeat.set(textureRepeat.x, textureRepeat.y)

            threeMaterial[textureKey] = texture
            threeMaterial.needsUpdate = true

            // Automatically enable transparency when alpha map is added
            if (textureKey === 'alphaMap' && !threeMaterial.transparent) {
              threeMaterial.transparent = true
              materials.get(name)!.transparent = true
            }

            // Propagate texture to variation clones
            const currentClones = get().variationClones.get(name)
            if (currentClones) {
              currentClones.forEach((clone) => {
                clone[textureKey] = texture
                if (textureKey === 'alphaMap' && !clone.transparent) {
                  clone.transparent = true
                }
                clone.needsUpdate = true
              })
            }
          })
        } else {
          threeMaterial[textureKey] = null as unknown as THREE.Texture
          threeMaterial.needsUpdate = true

          // Propagate null to variation clones
          const currentClones = get().variationClones.get(name)
          if (currentClones) {
            currentClones.forEach((clone) => {
              clone[textureKey] = null as unknown as THREE.Texture
              clone.needsUpdate = true
            })
          }
        }
      }

      if (properties.map !== undefined) {
        applyTexture(properties.map, 'map', true)
      }
      if (properties.normalMap !== undefined) {
        applyTexture(properties.normalMap, 'normalMap')
      }
      if (properties.roughnessMap !== undefined) {
        applyTexture(properties.roughnessMap, 'roughnessMap')
      }
      if (properties.metalnessMap !== undefined) {
        applyTexture(properties.metalnessMap, 'metalnessMap')
      }
      if (properties.emissiveMap !== undefined) {
        applyTexture(properties.emissiveMap, 'emissiveMap')
      }
      if (properties.alphaMap !== undefined) {
        applyTexture(properties.alphaMap, 'alphaMap')
      }

      // Update texture repeat if changed
      if (properties.textureRepeat !== undefined) {
        const repeat = properties.textureRepeat

        // Update all existing textures with new repeat values
        const updateTextureRepeat = (texture: THREE.Texture | null) => {
          if (texture) {
            texture.repeat.set(repeat.x, repeat.y)
            texture.needsUpdate = true
          }
        }

        updateTextureRepeat(threeMaterial.map)
        updateTextureRepeat(threeMaterial.normalMap)
        updateTextureRepeat(threeMaterial.roughnessMap)
        updateTextureRepeat(threeMaterial.metalnessMap)
        updateTextureRepeat(threeMaterial.emissiveMap)
        updateTextureRepeat(threeMaterial.alphaMap)
      }

      // Sync variation clones with non-texture property changes
      const clones = get().variationClones.get(name)
      if (clones && clones.size > 0) {
        const currentVariation = updated.colorVariation ?? 0

        if (properties.colorVariation !== undefined && properties.colorVariation <= 0) {
          // Variation turned off -- dispose all clones. Scene will reassign base material.
          get().clearVariationClones(name)
        } else {
          clones.forEach((clone, meshName) => {
            // Sync non-color properties
            if (properties.roughness !== undefined) clone.roughness = properties.roughness
            if (properties.metalness !== undefined) clone.metalness = properties.metalness
            if (properties.emissive !== undefined) clone.emissive.set(properties.emissive)
            if (properties.emissiveIntensity !== undefined)
              clone.emissiveIntensity = properties.emissiveIntensity
            if (properties.opacity !== undefined) clone.opacity = properties.opacity
            if (properties.transparent !== undefined) clone.transparent = properties.transparent

            // Recompute varied color if base color or variation amount changed
            if (properties.color !== undefined || properties.colorVariation !== undefined) {
              const variedColor = computeVariedColor(updated.color, meshName, currentVariation)
              clone.color.copy(variedColor)
            }

            clone.needsUpdate = true
          })
        }
      }

      // Force update
      threeMaterial.needsUpdate = true

      set({ materials: new Map(materials), isDirty: true })
    }
  },

  getMaterial: (name: string) => {
    return get().materials.get(name)
  },

  getThreeMaterial: (name: string) => {
    return get().materialInstances.get(name)
  },

  createVariedClone: (materialName: string, meshName: string) => {
    const { materialInstances, materials, variationClones } = get()
    const baseMaterial = materialInstances.get(materialName)
    const materialData = materials.get(materialName)
    if (!baseMaterial || !materialData) return undefined

    const variation = materialData.colorVariation ?? 0
    if (variation <= 0) return undefined

    // Clone the base material and apply lightness offset
    const clone = baseMaterial.clone()
    clone.name = `${materialName}__var__${meshName}`
    const variedColor = computeVariedColor(materialData.color, meshName, variation)
    clone.color.copy(variedColor)

    // Store the clone
    if (!variationClones.has(materialName)) {
      variationClones.set(materialName, new Map())
    }
    variationClones.get(materialName)!.set(meshName, clone)

    return clone
  },

  getVariedClone: (materialName: string, meshName: string) => {
    return get().variationClones.get(materialName)?.get(meshName)
  },

  clearVariationClones: (materialName: string) => {
    const { variationClones } = get()
    const clones = variationClones.get(materialName)
    if (clones) {
      clones.forEach((clone) => clone.dispose())
      clones.clear()
      variationClones.delete(materialName)
    }
  },

  exportToJSON: () => {
    const { materials } = get()
    const data = {
      version: '1.0',
      timestamp: new Date().toISOString(),
      materials: {} as Record<string, Omit<MaterialData, 'name'>>,
    }

    materials.forEach((material, name) => {
      // Convert base64 URLs to simple file paths for export
      const convertToPath = (url: string | null | undefined, type: string) => {
        if (!url) return undefined
        // If it's a base64 data URL, we'll note it needs to be saved manually
        if (url.startsWith('data:')) {
          console.warn(
            `Material "${name}" has embedded ${type} texture. Please save it to /public/textures/ manually.`
          )
          return `[EMBEDDED_${type.toUpperCase()}_NEEDS_MANUAL_SAVE]`
        }
        // If it's already a path, keep it
        return url
      }

      data.materials[name] = {
        color: material.color,
        roughness: material.roughness,
        metalness: material.metalness,
        emissive: material.emissive,
        emissiveIntensity: material.emissiveIntensity,
        opacity: material.opacity,
        transparent: material.transparent,
        // Include color variation if non-zero
        ...(material.colorVariation &&
          material.colorVariation > 0 && {
            colorVariation: material.colorVariation,
          }),
        // Only include texture paths, not base64 data
        ...(material.map && { map: convertToPath(material.map, 'color') }),
        ...(material.normalMap && { normalMap: convertToPath(material.normalMap, 'normal') }),
        ...(material.roughnessMap && {
          roughnessMap: convertToPath(material.roughnessMap, 'roughness'),
        }),
        ...(material.metalnessMap && {
          metalnessMap: convertToPath(material.metalnessMap, 'metalness'),
        }),
        ...(material.emissiveMap && {
          emissiveMap: convertToPath(material.emissiveMap, 'emissive'),
        }),
        ...(material.alphaMap && { alphaMap: convertToPath(material.alphaMap, 'alpha') }),
        // Include texture repeat settings
        ...(material.textureRepeat && { textureRepeat: material.textureRepeat }),
      }
    })

    return JSON.stringify(data, null, 2)
  },

  importFromJSON: (json: string) => {
    try {
      const data = JSON.parse(json)
      if (!data.materials) {
        throw new Error('Invalid material JSON format')
      }

      const { materials } = get()

      // Update existing materials with imported values
      Object.entries(data.materials).forEach(([name, props]) => {
        const material = materials.get(name)
        if (material) {
          get().updateMaterial(name, props as Partial<MaterialData>)
        }
      })

      console.log('Materials imported successfully')
    } catch (error) {
      console.error('Failed to import materials:', error)
      alert('Failed to import materials. Please check the JSON format.')
    }
  },

  downloadJSON: () => {
    const json = get().exportToJSON()
    const blob = new Blob([json], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `materials-${new Date().toISOString().split('T')[0]}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  },

  copyJSONToClipboard: () => {
    const json = get().exportToJSON()
    navigator.clipboard
      .writeText(json)
      .then(() => {
        console.log('Material JSON copied to clipboard')
      })
      .catch((err) => {
        console.error('Failed to copy to clipboard:', err)
      })
  },

  randomizeAllColors: () => {
    const { materials } = get()
    materials.forEach((material) => {
      get().updateMaterial(material.name, { color: randomColor() })
    })
  },

  resetAllToDefaults: () => {
    const { materials } = get()
    materials.forEach((material) => {
      const defaultMat = createDefaultMaterial(material.name)
      get().updateMaterial(material.name, defaultMat)
    })
  },

  loadTexture: async (materialName: string, textureType: keyof MaterialData, file: File) => {
    return new Promise((resolve) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        const url = e.target?.result as string
        get().updateMaterial(materialName, { [textureType]: url })
        resolve()
      }
      reader.readAsDataURL(file)
    })
  },

  removeTexture: (materialName: string, textureType: keyof MaterialData) => {
    get().updateMaterial(materialName, { [textureType]: null })
  },

  saveToServer: async () => {
    set({ isSaving: true })
    console.log('[MaterialStore] Save disabled in static mode')
    set({ isDirty: false, isSaving: false })
  },

  loadFromServer: async () => {
    try {
      const res = await fetch('/data/material-config.json')
      if (res.ok) {
        const text = await res.text()
        if (text) {
          const data = JSON.parse(text)
          if (data.materials) {
            get().importFromJSON(text)
            set({ isDirty: false })
          }
        }
      }
    } catch {
      console.log('[MaterialStore] Config not available, using defaults')
    }
  },
}))
